import { useMemo, useState } from 'react'
import {
  Box, Breadcrumbs, Typography, Stack, Button, Chip, IconButton, Menu, MenuItem,
  ListItemIcon, ListItemText, Snackbar, Alert, Dialog, DialogTitle, DialogContent, DialogActions, TextField,
} from '@mui/material'
import { Add, MoreVert, Edit, Block, ContentCopy } from '@mui/icons-material'
import { DataGrid, type GridColDef } from '@mui/x-data-grid'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { listRoles, deactivateRole, cloneRole, type RoleListItem } from '../../api/admin'
import { errorMessage } from '../../api/client'
import RoleFormDialog from './RoleFormDialog'

export default function RolesPage() {
  const [formTarget, setFormTarget] = useState<RoleListItem | null | 'new'>(null)
  const [cloneTarget, setCloneTarget] = useState<RoleListItem | null>(null)
  const [menuAnchor, setMenuAnchor] = useState<{ el: HTMLElement; row: RoleListItem } | null>(null)
  const [toast, setToast] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const qc = useQueryClient()
  const { data, isLoading } = useQuery({ queryKey: ['admin', 'roles'], queryFn: listRoles })

  const closeMenu = () => setMenuAnchor(null)

  const deactivateMutation = useMutation({
    mutationFn: deactivateRole,
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['admin', 'roles'] }); setToast('Rol dezactivat.') },
    onError: (e) => setError(errorMessage(e)),
  })

  const columns = useMemo<GridColDef<RoleListItem>[]>(() => [
    {
      field: 'name', headerName: 'Nume', flex: 1.2, minWidth: 180,
      renderCell: (p) => (
        <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center' }}>
          <Typography variant="body2">{p.row.name}</Typography>
          {p.row.isSystem && <Chip label="sistem" size="small" variant="outlined" sx={{ height: 18, fontSize: 10 }} />}
        </Stack>
      ),
    },
    { field: 'code', headerName: 'Cod', width: 160, renderCell: (p) => <code>{p.row.code}</code> },
    { field: 'description', headerName: 'Descriere', flex: 1.5, minWidth: 220 },
    { field: 'permissionCount', headerName: 'Permisiuni', width: 110, type: 'number' },
    { field: 'userCount', headerName: 'Utilizatori', width: 110, type: 'number' },
    {
      field: 'status', headerName: 'Status', width: 100, sortable: false,
      renderCell: (p) => p.row.isActive
        ? <Chip label="Activ" size="small" color="success" variant="outlined" />
        : <Chip label="Inactiv" size="small" variant="outlined" />,
    },
    {
      field: 'actions', headerName: '', width: 56, sortable: false, filterable: false, disableColumnMenu: true,
      renderCell: (p) => (
        <IconButton size="small" onClick={(e) => setMenuAnchor({ el: e.currentTarget, row: p.row })}>
          <MoreVert fontSize="small" />
        </IconButton>
      ),
    },
  ], [])

  return (
    <Stack spacing={2}>
      <Breadcrumbs separator="/" sx={{ fontSize: 12.5 }}>
        <Typography variant="inherit" color="text.secondary">Administrare</Typography>
        <Typography variant="inherit" color="text.primary">Roluri și permisiuni</Typography>
      </Breadcrumbs>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1.5 }}>
        <Typography variant="h2">Roluri și permisiuni</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={() => setFormTarget('new')}>Adaugă rol</Button>
      </Box>

      <Box sx={{ height: 520, bgcolor: 'background.paper', borderRadius: 1.5 }}>
        <DataGrid
          rows={data ?? []} columns={columns} loading={isLoading} density="comfortable"
          disableRowSelectionOnClick
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
          pageSizeOptions={[25, 50]}
        />
      </Box>

      <RoleFormDialog role={formTarget} onClose={() => setFormTarget(null)} />
      <CloneRoleDialog role={cloneTarget} onClose={() => setCloneTarget(null)} />

      <Menu anchorEl={menuAnchor?.el} open={!!menuAnchor} onClose={closeMenu}>
        {menuAnchor && [
          <MenuItem key="edit" onClick={() => { setFormTarget(menuAnchor.row); closeMenu() }}>
            <ListItemIcon><Edit fontSize="small" /></ListItemIcon><ListItemText>{menuAnchor.row.isSystem ? 'Vizualizare' : 'Editare'}</ListItemText>
          </MenuItem>,
          <MenuItem key="clone" onClick={() => { setCloneTarget(menuAnchor.row); closeMenu() }}>
            <ListItemIcon><ContentCopy fontSize="small" /></ListItemIcon><ListItemText>Clonează</ListItemText>
          </MenuItem>,
          !menuAnchor.row.isSystem && (
            <MenuItem key="deactivate" disabled={!menuAnchor.row.isActive}
              onClick={() => { closeMenu(); deactivateMutation.mutate(menuAnchor.row.id) }}>
              <ListItemIcon><Block fontSize="small" /></ListItemIcon><ListItemText>Dezactivare</ListItemText>
            </MenuItem>
          ),
        ]}
      </Menu>

      <Snackbar open={!!toast} autoHideDuration={3000} onClose={() => setToast(null)} message={toast} />
      <Snackbar open={!!error} autoHideDuration={5000} onClose={() => setError(null)}>
        <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>
      </Snackbar>
    </Stack>
  )
}

/** FR-ADM-026 — punct de plecare pentru un rol nou, pornind de la permisiunile unuia existent. */
function CloneRoleDialog({ role, onClose }: { role: RoleListItem | null; onClose: () => void }) {
  const [code, setCode] = useState('')
  const [name, setName] = useState('')
  const qc = useQueryClient()

  const mutation = useMutation({
    mutationFn: () => cloneRole(role!.id, code, name),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['admin', 'roles'] }); setCode(''); setName(''); onClose() },
  })

  return (
    <Dialog open={!!role} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Clonare rol — {role?.name}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          <Typography variant="body2" color="text.secondary">
            Se creează un rol nou cu aceleași permisiuni ca „{role?.name}".
          </Typography>
          <TextField label="Cod nou" value={code} onChange={(e) => setCode(e.target.value)} autoFocus fullWidth />
          <TextField label="Nume nou" value={name} onChange={(e) => setName(e.target.value)} fullWidth />
          {mutation.isError && <Alert severity="error">{errorMessage(mutation.error)}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose}>Renunță</Button>
        <Button variant="contained" disabled={!code || !name || mutation.isPending} onClick={() => mutation.mutate()}>
          Clonează
        </Button>
      </DialogActions>
    </Dialog>
  )
}
